import React, { useState, useEffect, useMemo, useRef } from 'react';
import './OnboardingModal.css';

const OnboardingModal = ({ user, isOpen, onClose, onComplete, onQuestionAnswered, supabaseHelpers, unansweredQuestions = [] }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({
    ideal_saturday_response: '',
    inspiration_learning_response: '',
    passion_discussion_response: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [backgroundProcessing, setBackgroundProcessing] = useState(new Set());
  const inputRef = useRef(null);

  // All questions definition
  const allQuestions = [
    {
      number: 1,
      key: 'ideal_saturday_response',
      title: "What's your ideal way to spend a perfect Saturday afternoon?",
      placeholder: "Reading at a cozy coffee shop, then browsing a bookstore...",
      context: 'ideal_saturday'
    },
    {
      number: 2,
      key: 'inspiration_learning_response', 
      title: "Where do you go when you want to learn something new or feel inspired?",
      placeholder: "Libraries are my sanctuary, plus local workshops...",
      context: 'learning_inspiration'
    },
    {
      number: 3,
      key: 'passion_discussion_response',
      title: "What's something you could talk about passionately for hours, and where do you usually end up discussing it?",
      placeholder: "Craft beer - I love brewery tours and beer gardens...",
      context: 'passion_venues'
    }
  ];

  // Filter to only show unanswered questions - using useMemo for reactivity
  const questionsToAsk = useMemo(() => {
    const filtered = allQuestions.filter(q => unansweredQuestions.includes(q.context));
    console.log('🎯 OnboardingModal: Filtering questions', {
      allQuestions: allQuestions.map(q => q.context),
      unansweredQuestions,
      questionsToAsk: filtered.map(q => q.context),
      questionsToAskLength: filtered.length
    });
    return filtered;
  }, [unansweredQuestions]);

  // Reset question index when questions list changes
  useEffect(() => {
    if (questionsToAsk.length > 0 && currentQuestionIndex >= questionsToAsk.length) {
      console.log('🎯 OnboardingModal: Resetting question index from', currentQuestionIndex, 'to 0 (out of bounds)');
      setCurrentQuestionIndex(0);
    } else if (questionsToAsk.length > 0 && currentQuestionIndex > 0) {
      // Always reset to 0 when the questions list changes (after an answer is submitted)
      // This ensures we show the first remaining question
      console.log('🎯 OnboardingModal: Resetting question index from', currentQuestionIndex, 'to 0 (new question list)');
      setCurrentQuestionIndex(0);
    }
  }, [questionsToAsk.length]); // Only depend on length to avoid infinite loops

  // Auto-focus the input when modal opens or question changes
  useEffect(() => {
    if (isOpen && inputRef.current && !isSubmitting) {
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, currentQuestionIndex, isSubmitting]);

  // Current question data
  const currentQuestionData = questionsToAsk[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questionsToAsk.length - 1;
  
  console.log('🎯 OnboardingModal: Current state', {
    questionsToAskCount: questionsToAsk.length,
    currentQuestionIndex,
    currentQuestionContext: currentQuestionData?.context,
    currentQuestionTitle: currentQuestionData?.title?.substring(0, 50) + '...',
    isLastQuestion
  });
  
  // If no questions to ask, close modal
  if (questionsToAsk.length === 0) {
    console.log('🎯 OnboardingModal: No questions to ask, closing modal');
    if (isOpen && onComplete) {
      onComplete();
    }
    return null;
  }

  // Safety check for current question data
  if (!currentQuestionData) {
    console.error('🎯 OnboardingModal: No current question data available');
    return null;
  }

  const handleResponseChange = (value) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestionData.key]: value
    }));
  };

  const handleNext = async () => {
    const response = responses[currentQuestionData.key].trim();
    if (response.length < 10) {
      setError('Please tell us a bit more! A sentence or two would be great.');
      return;
    }
    if (response.length > 280) {
      setError('Love the enthusiasm! Could you summarize in fewer words?');
      return;
    }
    
    setError(null);
    setIsSubmitting(true);

    try {
      // Submit this answer immediately
      await submitCurrentAnswer();
      
      // Notify parent that this specific question was answered
      if (onQuestionAnswered) {
        console.log('🎯 OnboardingModal: Notifying parent that question answered:', currentQuestionData.context);
        onQuestionAnswered(currentQuestionData.context);
      }
      
      // Note: We don't manually increment currentQuestionIndex here because:
      // 1. The parent (OnboardingManager) will update the unansweredQuestions array
      // 2. This will filter out the answered question from questionsToAsk
      // 3. The useEffect will reset currentQuestionIndex to 0 if needed
      // 4. The next question in the filtered array will automatically be shown
      
      if (isLastQuestion) {
        // All questions completed, trigger onComplete callback
        console.log('🎯 OnboardingModal: Last question completed, calling onComplete');
        onComplete();
      } else {
        // Let the parent manage the flow by updating unansweredQuestions
        console.log('🎯 OnboardingModal: Question answered, letting parent manage flow');
        
        // Focus will be handled by the useEffect when the question changes
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError('Something went wrong saving your answer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const submitCurrentAnswer = async () => {
    const response = responses[currentQuestionData.key].trim();
    
    try {
      console.log(`🎯 OnboardingModal: Submitting answer for ${currentQuestionData.key}:`, response);
      
      // Step 1: Save the basic response immediately (fast)
      try {
        console.log(`💾 Saving answer to database immediately...`);
        await saveSingleAnswer(currentQuestionData.key, response);
        console.log(`✅ Database save completed - user can continue`);
      } catch (dbError) {
        console.error(`❌ Database save failed:`, dbError);
        
        // If database fails due to auth issues, save locally for now
        console.log(`💾 Saving locally as fallback...`);
        localStorage.setItem(`onboarding_${currentQuestionData.key}`, JSON.stringify({
          response,
          timestamp: new Date().toISOString(),
          user_id: user.id || user.sub
        }));
        console.log(`✅ Saved locally - user can continue`);
      }
      
      // Step 2: Process with LLM in the background (don't await)
      console.log(`🤖 Starting background LLM processing...`);
      setBackgroundProcessing(prev => new Set([...prev, currentQuestionData.key]));
      processResponseWithLLMBackground(response, currentQuestionData.context, currentQuestionData.key);
      
      console.log(`✅ OnboardingModal: Answer submitted, LLM processing in background`);
    } catch (error) {
      console.error(`❌ OnboardingModal: Error submitting ${currentQuestionData.key}:`, error);
      throw new Error(`Failed to save your answer. Please try again. (${error.message})`);
    }
  };

        // Background LLM processing - doesn't block the UI
  const processResponseWithLLMBackground = async (response, context, questionKey) => {
    try {
      console.log(`🤖 [Background] Processing "${response.substring(0, 30)}..." with LLM`);
      
      // Call OpenAI API
      const llmResult = await callOpenAIAPI(response, context);
      console.log(`✅ [Background] LLM processing completed for ${questionKey}:`, llmResult);
      
      // Store interests in new unified system
      try {
        const { createClient } = await import('@supabase/supabase-js');
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
        
        if (supabaseUrl && supabaseKey) {
          const directSupabase = createClient(supabaseUrl, supabaseKey);
          
          // Map question keys to source references
          const sourceMap = {
            'ideal_saturday_response': 'ideal_saturday',
            'inspiration_learning_response': 'learning_inspiration',
            'passion_discussion_response': 'passion_venues'
          };
          
          const sourceReference = sourceMap[questionKey] || questionKey;
          
          // Save Google Places interests
          if (llmResult.google_places_categories?.length > 0) {
            for (const category of llmResult.google_places_categories) {
              try {
                // Add friendly display name
                const displayName = category.replace(/_/g, ' ')
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
                
                await directSupabase
                  .from('user_interests')
                  .upsert({
                    user_id: user.id || user.sub,
                    interest_type: 'google_places_type',
                    interest_value: category,
                    display_name: displayName,
                    source_type: 'question_response',
                    source_reference: sourceReference,
                    confidence_score: llmResult.confidence_score || 0.8,
                    updated_at: new Date().toISOString()
                  }, {
                    onConflict: 'user_id,interest_type,interest_value'
                  });
                
                console.log(`✅ [Background] Saved Google Places interest: ${category}`);
              } catch (prefError) {
                console.warn(`⚠️ [Background] Could not save interest for ${category}:`, prefError);
              }
            }
          }
          
          // Save category tag interests
          if (llmResult.category_tags?.length > 0) {
            for (const tag of llmResult.category_tags) {
              try {
                await directSupabase
                  .from('user_interests')
                  .upsert({
                    user_id: user.id || user.sub,
                    interest_type: 'category_tag',
                    interest_value: tag,
                    display_name: tag,
                    source_type: 'question_response',
                    source_reference: sourceReference,
                    confidence_score: llmResult.confidence_score || 0.8,
                    updated_at: new Date().toISOString()
                  }, {
                    onConflict: 'user_id,interest_type,interest_value'
                  });
                
                console.log(`✅ [Background] Saved category tag interest: ${tag}`);
              } catch (tagError) {
                console.warn(`⚠️ [Background] Could not save tag for ${tag}:`, tagError);
              }
            }
            console.log(`✅ [Background] Saved ${llmResult.google_places_categories.length} Google Places preferences`);
          }
        }
      } catch (updateError) {
        console.error(`❌ [Background] Could not update database with LLM results:`, updateError);
      }
      
      // Log the processing
      try {
        const { createClient } = await import('@supabase/supabase-js');
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
        
        if (supabaseUrl && supabaseKey) {
          const directSupabase = createClient(supabaseUrl, supabaseKey);
          
          await directSupabase
            .from('llm_processing_logs')
            .insert([{
              user_id: user.id || user.sub,
              user_response: response,
              question_context: context,
              llm_model: 'gpt-4-turbo-preview',
              llm_response: JSON.stringify(llmResult),
              extracted_tags: llmResult.category_tags || [],
              extracted_google_types: llmResult.google_places_categories || [],
              confidence_score: llmResult.confidence_score || 0,
              success: true
            }]);
          
          console.log(`✅ [Background] Logged LLM processing`);
        }
      } catch (logError) {
        console.warn(`⚠️ [Background] Could not log LLM processing:`, logError);
      }
      
    } catch (error) {
      console.error(`❌ [Background] LLM processing failed for ${questionKey}:`, error);
      // Background processing failure shouldn't affect user experience
    } finally {
      // Remove from background processing set
      setBackgroundProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionKey);
        return newSet;
      });
    }
  };

  const saveSingleAnswer = async (questionKey, response) => {
    try {
      console.log(`💾 Attempting to save ${questionKey} to database using unified system...`);
      
      // Map question keys to the unified system
      const questionMap = {
        'ideal_saturday_response': {
          key: 'ideal_saturday',
          text: "What's your ideal way to spend a perfect Saturday afternoon?"
        },
        'inspiration_learning_response': {
          key: 'learning_inspiration',
          text: "Where do you go when you want to learn something new or feel inspired?"
        },
        'passion_discussion_response': {
          key: 'passion_venues',
          text: "What's something you could talk about passionately for hours, and where do you usually end up discussing it?"
        }
      };
      
      const questionInfo = questionMap[questionKey];
      if (!questionInfo) {
        throw new Error(`Unknown question key: ${questionKey}`);
      }
      
      // Use new unified system via supabaseHelpers
      if (supabaseHelpers?.saveProfileResponse) {
        console.log(`💾 Using unified system via supabaseHelpers...`);
        const result = await supabaseHelpers.saveProfileResponse(
          user.id || user.sub,
          questionInfo.key,
          questionInfo.text,
          response,
          'onboarding'
        );
        
        if (result.error) {
          throw new Error(`Database save failed: ${result.error.message}`);
        }
        
        console.log(`✅ Successfully saved ${questionKey} to database using unified system`);
        return result.data;
      }
      
      // Fallback: direct Supabase client for new unified tables
      console.log(`💾 Using direct Supabase access for unified tables...`);
      const { createClient } = await import('@supabase/supabase-js');
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Supabase environment variables not configured');
      }
      
      const directSupabase = createClient(supabaseUrl, supabaseKey);
      
      // Map question keys to display order for consistent profile ordering
      const displayOrderMap = {
        'ideal_saturday': 1,
        'learning_inspiration': 2,
        'passion_venues': 3
      };
      
      // Save to new unified user_profile_responses table
      const { data, error } = await directSupabase
        .from('user_profile_responses')
        .upsert({
          user_id: user.id || user.sub,
          question_key: questionInfo.key,
          question_text: questionInfo.text,
          question_category: 'onboarding',
          display_order: displayOrderMap[questionInfo.key] || null,
          user_answer: response,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,question_key'
        })
        .select()
        .single();
      
      if (error) {
        throw new Error(`Database error: ${error.message}`);
      }
      
      console.log(`✅ Saved ${questionKey} to unified database table`);
      return data;
      
    } catch (error) {
      console.error(`❌ Error saving ${questionKey}:`, error);
      throw new Error(`Database save failed: ${error.message}`);
    }
  };



  const processResponseWithLLM = async (response, context, questionKey) => {
    const startTime = Date.now();
    
    try {
      console.log(`🤖 Calling OpenAI API for: "${response.substring(0, 50)}..."`);
      
      // Call OpenAI API to process the response
      const llmResult = await callOpenAIAPI(response, context);
      console.log(`🤖 OpenAI result:`, llmResult);
      
      // Log the processing (skip if supabaseHelpers not available)
      try {
        if (supabaseHelpers?.logEvent) {
          await logLLMProcessing({
            user_id: user.id || user.sub,
            user_response: response,
            question_context: context,
            llm_model: 'gpt-4-turbo-preview',
            llm_response: JSON.stringify(llmResult),
            processing_duration_ms: Date.now() - startTime,
            extracted_tags: llmResult.category_tags || [],
            extracted_google_types: llmResult.google_places_categories || [],
            confidence_score: llmResult.confidence_score || 0,
            success: true
          });
        }
      } catch (logError) {
        console.warn(`⚠️ Could not log LLM processing:`, logError);
      }

      // Save Google Places preferences (skip if supabaseHelpers not available)
      try {
        if (llmResult.google_places_categories?.length > 0 && supabaseHelpers?.createOrUpdateUserGooglePlacesPreference) {
          await saveGooglePlacesPreferences(llmResult.google_places_categories, response, questionKey);
        }
      } catch (prefError) {
        console.warn(`⚠️ Could not save Google Places preferences:`, prefError);
      }

    } catch (error) {
      console.error('❌ LLM processing error:', error);
      
      // Log the error (skip if supabaseHelpers not available)
      try {
        if (supabaseHelpers?.logEvent) {
          await logLLMProcessing({
            user_id: user.id || user.sub,
            user_response: response,
            question_context: context,
            processing_duration_ms: Date.now() - startTime,
            success: false,
            error_message: error.message
          });
        }
      } catch (logError) {
        console.warn(`⚠️ Could not log LLM error:`, logError);
      }
      
      throw error; // Re-throw to handle in submitCurrentAnswer
    }
  };

  const callOpenAIAPI = async (userResponse, questionContext) => {
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    if (!apiKey) {
      throw new Error('OpenAI API key not configured');
    }

    const googlePlacesTypes = [
      // Food & Dining
      'restaurant', 'meal_takeaway', 'meal_delivery', 'cafe', 'bar', 'bakery',
      'food', 'american_restaurant', 'chinese_restaurant', 'french_restaurant',
      'indian_restaurant', 'italian_restaurant', 'japanese_restaurant',
      'korean_restaurant', 'mexican_restaurant', 'thai_restaurant',
      'vietnamese_restaurant', 'greek_restaurant', 'spanish_restaurant',
      'turkish_restaurant', 'seafood_restaurant', 'steak_house',
      'vegetarian_restaurant', 'pizza_restaurant', 'hamburger_restaurant',
      'sandwich_shop', 'ice_cream_shop', 'coffee_shop', 'tea_house',
      'wine_bar', 'cocktail_lounge', 'brewery', 'distillery',
      
      // Entertainment & Activities
      'movie_theater', 'bowling_alley', 'amusement_park', 'aquarium',
      'art_gallery', 'museum', 'zoo', 'park', 'tourist_attraction',
      'night_club', 'casino', 'spa', 'gym', 'library', 'book_store',
      'music_venue', 'performing_arts_theater',
      
      // Shopping & Services
      'shopping_mall', 'department_store', 'clothing_store', 'electronics_store',
      'grocery_store', 'supermarket', 'convenience_store', 'pharmacy',
      'beauty_salon', 'hair_care', 'florist',
      
      // Professional & Learning
      'school', 'university', 'library', 'community_center', 'convention_center',
      'coworking_space',
      
      // Outdoor & Recreation
      'campground', 'rv_park', 'marina', 'golf_course', 'ski_resort',
      'hiking_area', 'national_park', 'beach', 'lake', 'mountain'
    ];

    const contextualInstructions = {
      ideal_saturday: "Focus on leisure, entertainment, recreation, outdoor activities, dining, and shopping venues where Saturday activities happen.",
      learning_inspiration: "Prioritize educational, cultural, professional, and intellectual venues including libraries, museums, coworking spaces, and community centers.",
      passion_venues: "Map both the passion topic AND the social venues where these discussions typically occur. Consider atmosphere preferences (quiet vs. social)."
    };

    const prompt = `
You are an expert at mapping user interests to Google Places API types.

Context: User is answering a ${questionContext} question.
${contextualInstructions[questionContext] || ''}

User response: "${userResponse}"

Available Google Places types: ${googlePlacesTypes.join(', ')}

Extract relevant information and respond with ONLY valid JSON:
{
  "category_tags": ["human-readable interest tags"],
  "google_places_categories": ["valid_google_places_types"],
  "confidence_score": 0.0-1.0
}

Rules:
- Only use Google Places types from the provided list
- Maximum 5 category_tags and 5 google_places_categories
- Confidence score based on clarity and specificity of user response
- If no clear food/place interests, return empty arrays
`;

    const requestData = {
      model: "gpt-4-turbo-preview",
      messages: [{ role: "user", content: prompt }],
      temperature: 0.1,
      max_tokens: 500,
      response_format: { type: "json_object" }
    };

    // Add to debug panel
    if (window.adminDebug?.addApiCall) {
      window.adminDebug.addApiCall(
        'https://api.openai.com/v1/chat/completions',
        'POST',
        requestData,
        null, // Response will be added after
        0 // Duration will be updated
      );
    }

    const startTime = Date.now();
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify(requestData)
    });

    const duration = Date.now() - startTime;
    
    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`OpenAI API error: ${response.status} - ${errorData}`);
    }

    const responseData = await response.json();
    
    // Update debug panel with response
    if (window.adminDebug?.addApiCall) {
      window.adminDebug.addApiCall(
        'https://api.openai.com/v1/chat/completions',
        'POST',
        requestData,
        responseData,
        duration
      );
    }

    try {
      const parsed = JSON.parse(responseData.choices[0].message.content);
      
      // Validate and clean the response
      return {
        category_tags: (parsed.category_tags || []).slice(0, 5),
        google_places_categories: (parsed.google_places_categories || [])
          .filter(type => googlePlacesTypes.includes(type))
          .slice(0, 5),
        confidence_score: Math.min(Math.max(parsed.confidence_score || 0, 0), 1)
      };
    } catch (parseError) {
      throw new Error(`Failed to parse OpenAI response: ${parseError.message}`);
    }
  };

  const logLLMProcessing = async (logData) => {
    try {
      await supabaseHelpers.logEvent('llm_processing', logData, user.id || user.sub);
    } catch (error) {
      console.error('Error logging LLM processing:', error);
    }
  };

  const saveGooglePlacesPreferences = async (googlePlacesCategories, response, questionKey) => {
    try {
      for (const category of googlePlacesCategories) {
        await supabaseHelpers.createOrUpdateUserGooglePlacesPreference({
          user_id: user.id || user.sub,
          included_type: category,
          source_response: response,
          source_question: questionKey,
          confidence_score: 0.8, // Default confidence
          llm_model_used: 'gpt-4-turbo-preview'
        });
      }
    } catch (error) {
      console.error('Error saving Google Places preferences:', error);
    }
  };



  const checkIfAllQuestionsAnswered = async (newResponses) => {
    try {
      const { data: profile } = await supabaseHelpers.getUserInterestProfile(user.id || user.sub);
      
      const allFields = [
        'ideal_saturday_response',
        'inspiration_learning_response', 
        'passion_discussion_response'
      ];

      return allFields.every(field => 
        (profile && profile[field]?.trim()) || newResponses[field]?.trim()
      );
    } catch (error) {
      console.error('Error checking if all questions answered:', error);
      return false;
    }
  };

  const handleClose = () => {
    console.log('🎯 OnboardingModal: User closed modal - progress preserved');
    onClose();
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on the modal content
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNext();
    }
  };

  if (!isOpen) return null;

  // Calculate the total questions and current position for progress display
  const totalQuestions = allQuestions.length;
  const currentPosition = totalQuestions - questionsToAsk.length + currentQuestionIndex + 1;
  
  console.log('🎯 OnboardingModal: Progress calculation', {
    totalQuestions,
    questionsToAskLength: questionsToAsk.length,
    currentQuestionIndex,
    currentPosition,
    progressText: `${currentPosition} of ${totalQuestions}`
  });

  return (
    <div className="onboarding-modal-overlay" onClick={handleOverlayClick}>
      <div className="onboarding-modal">
        <div className="onboarding-header">
          <div className="progress-indicator">
            <span className="progress-text">{currentPosition} of {totalQuestions}</span>
            <div className="progress-dots">
              {questionsToAsk.map((question, index) => (
                <div 
                  key={index} 
                  className={`progress-dot ${index <= currentQuestionIndex ? 'active' : ''}`}
                />
              ))}
            </div>
          </div>
          <div className="value-proposition">
            <p className="value-text">
              Help us discover your perfect places and people to connect with
            </p>
          </div>
        </div>

        <div className="onboarding-content">
          <h2 className="question-title">{currentQuestionData.title}</h2>
          
          <input
            ref={inputRef}
            type="text"
            className="response-input"
            placeholder={currentQuestionData.placeholder}
            value={responses[currentQuestionData.key]}
            onChange={(e) => handleResponseChange(e.target.value)}
            onKeyPress={handleKeyPress}
            maxLength={280}
            disabled={isSubmitting}
          />
          
          <div className="character-count">
            {responses[currentQuestionData.key].length}/280
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}
        </div>

        <div className="onboarding-actions">
          {currentQuestionIndex > 0 && (
            <button 
              onClick={handleBack} 
              className="btn-back"
              disabled={isSubmitting}
            >
              ← Back
            </button>
          )}
          
          <button 
            onClick={handleNext} 
            className="btn-continue"
            disabled={isSubmitting || responses[currentQuestionData.key].trim().length < 10}
          >
            {isSubmitting ? 'Saving...' : isLastQuestion ? 'Start Exploring ✨' : 'Continue →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal; 